'use client';

import React, { createContext, useContext, useMemo } from 'react';
import { BLOCKS, Document as RTDocument } from '@contentful/rich-text-types';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { TypeSettingsWithoutUnresolvableLinksResponse } from '@shared/features/contentful/_generated';
import createSettingsObject from '@shared/utils/createSettingsObject';
import { getLabel as getLabelUtil, isRichText } from '@shared/utils/getLabel';

import { clientLogger } from '@shared/utils/clientLogger';
import { TUserCountryData } from '@shared/utils/getClientIp';

const { warn: logWarn } = clientLogger('SettingsContext');

export type TSettingsValue = string | boolean | ISettings | RTDocument | undefined;
export interface ISettings {
    [key: string]: TSettingsValue;
}

interface ISettingsContext {
    contentfulSettings: ISettings;
    lang: string;
    getLabel: (keys: string[], componentSettings?: ISettings) => string;
    getRichLabel: (keys: string[], componentSettings?: ISettings) => RTDocument;
    getList: (keys: string[]) => Array<{ key: string; value: string }>;
    getLabelsForCommonKey: (
        key: string,
        subKeys: string[],
        componentSettings?: ISettings,
    ) => { [key: string]: string };
    userIpCountryData?: TUserCountryData;
}

export const SettingsContext = createContext<ISettingsContext | undefined>(undefined);

interface ISettingsProvider {
    children: React.ReactNode;
    settings?: TypeSettingsWithoutUnresolvableLinksResponse[];
    resolvedSettings?: ISettings;
    lang: string;
    countryData?: TUserCountryData;
}

const createFallbackRichText = (message: string): RTDocument => ({
    nodeType: BLOCKS.DOCUMENT,
    data: {},
    content: [
        {
            nodeType: BLOCKS.PARAGRAPH,
            data: {},
            content: [
                {
                    nodeType: 'text',
                    value: message,
                    marks: [],
                    data: {},
                },
            ],
        },
    ],
});

export const SettingsProvider = ({
    children,
    settings,
    resolvedSettings,
    lang,
    countryData,
}: ISettingsProvider) => {
    const contentfulSettings: ISettings = useMemo(
        () => resolvedSettings ?? createSettingsObject(settings),
        [resolvedSettings, settings],
    );
    const getLabel = useMemo(() => {
        return (keys: string[], componentSettings?: ISettings): string => {
            const base = componentSettings || contentfulSettings;
            const result = getLabelUtil(keys, base);
            const fallback = keys.join('.');

            if (result === undefined) {
                return fallback;
            }

            if (typeof result === 'object' && result !== null && 'nodeType' in result) {
                return documentToPlainTextString(result);
            }

            return result as string;
        };
    }, [contentfulSettings]);

    const getRichLabel = useMemo(() => {
        return (keys: string[], componentSettings?: ISettings): RTDocument => {
            const base = componentSettings || contentfulSettings;
            const result = getLabelUtil(keys, base);
            const fallback = keys.join('.');

            if (typeof result === 'object' && result !== null && 'nodeType' in result) {
                return result;
            }

            if (typeof result === 'string') {
                return createFallbackRichText(result);
            }

            return createFallbackRichText(fallback);
        };
    }, [contentfulSettings]);

    const getList = useMemo(() => {
        return (keys: string[]): Array<{ key: string; value: string }> => {
            let current: TSettingsValue = contentfulSettings;
            for (const key of keys) {
                if (
                    current &&
                    typeof current === 'object' &&
                    !isRichText(current) &&
                    key in current
                ) {
                    current = current[key];
                } else {
                    // no list: debug info
                    logWarn('Settings list missing or invalid', { keys, found: current }, true);
                    return [];
                }
            }
            if (current && typeof current === 'object') {
                return Object.entries(current)
                    .filter(([, v]) => typeof v === 'string')
                    .map(([k, v]) => ({ key: k, value: v as string }));
            }
            return [];
        };
    }, [contentfulSettings]);

    const getLabelsForCommonKey = useMemo(() => {
        return (key: string, subKeys: string[], componentSettings?: ISettings) => {
            const labels: { [key: string]: string } = {};
            subKeys.forEach((subKey) => {
                labels[subKey] = getLabel([key, subKey], componentSettings);
            });
            return labels;
        };
    }, [getLabel]);

    const contextValue = useMemo(
        () => ({
            contentfulSettings,
            lang,
            getLabel,
            getRichLabel,
            getList,
            getLabelsForCommonKey,
            userIpCountryData: countryData,
        }),
        [
            contentfulSettings,
            lang,
            getLabel,
            getList,
            getLabelsForCommonKey,
            getRichLabel,
            countryData,
        ],
    );

    return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>;
};

export const useSettingsContext = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettingsContext must be used within a SettingsProvider');
    }
    return context;
};
